import { IconCalendarPlus, IconCheck, IconShoppingBag } from '@tabler/icons-react'
import clsx from 'clsx'
import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert } from 'src/components/Alert/Alert'
import { Button } from 'src/components/Button/Button'
import { BuyBoxPrice } from 'src/components/BuyBox/BuyBoxPrice'
import { ShippingInfo } from 'src/components/BuyBox/ShippingInfo'
import { IconLoading } from 'src/components/Icons/IconLoading'
import { Modal } from 'src/components/Modal/Modal'
import { Stepper } from 'src/components/Stepper/Stepper'
import { Text } from 'src/components/Text/Text'
import { PurchaseType } from 'src/constants'
import { useLocale } from 'src/contexts/LocaleContext'
import { graphQLClient } from 'src/data/graphql/graphql-client'
import { useProductStockQuery, type Product, type ProductVariant } from 'src/data/graphql-generated'
import { addToCartAction, addToSubscriptionAction } from 'src/functions/cart/addToCart'
import { getMetafield } from 'src/functions/util/getMetafield'
import { useCartDiscountCodesUpdateMutation } from 'src/hooks/useCartDiscountCodesUpdateMutation'
import { SHOW_ADDED_TO_CART_IN_MILLISECONDS, useCartLinesAddMutation } from 'src/hooks/useCartLinesAddMutation'
import { useCartLinesUpdateMutation } from 'src/hooks/useCartLinesUpdateMutation'
import { useCartQuery } from 'src/hooks/useCartQuery'
import { useTimer } from 'src/hooks/useTimer'

import styles from './BuyBox.module.scss'

import type { ProductSubscriptionSellingPlans } from 'src/types/Subscription'

interface BuyBoxProps {
  product: Product
  variant: ProductVariant
  purchaseType: PurchaseType
  generatedSellingPlans?: ProductSubscriptionSellingPlans
  sellingPlanInterval?: number
  isRenderedWithinModal?: boolean
}

export const BuyBox = ({
  product,
  variant,
  isRenderedWithinModal = false,
  purchaseType,
  generatedSellingPlans,
  sellingPlanInterval = 0,
}: BuyBoxProps) => {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [quantity, setQuantity] = useState(1)
  const [showShippingTermsModal, setShowShippingTermsModal] = useState(false)
  const { data: cart } = useCartQuery()
  const { mutateAsync: cartDiscountCodesUpdate } = useCartDiscountCodesUpdateMutation()
  const { mutateAsync: cartLinesUpdate } = useCartLinesUpdateMutation()
  const { mutateAsync: addToCart, isPending: isAddToCartBusy } = useCartLinesAddMutation()
  const { data: stockInfo, isLoading: stockInfoLoading } = useProductStockQuery(graphQLClient, { id: product.id })
  const { setTimer } = useTimer(SHOW_ADDED_TO_CART_IN_MILLISECONDS)
  const [showCheck, setShowCheck] = useState(false)

  const { productType } = product
  const {
    lines: { nodes: existingLines },
  } = cart || { id: '', lines: { nodes: [] } }
  const { sellingPlans = {} } = generatedSellingPlans || {}

  const isBusy = isAddToCartBusy || stockInfoLoading

  const variantAvailable = stockInfo?.product?.variants.nodes.find(
    (stockVariant) => stockVariant.id === variant.id,
  )?.availableForSale

  const addToCartText = t('common.addToCart')
  const notAvailableText = t('common.notAvailable')
  const addToCartSuccessText = t('common.addToCartSuccess')
  const addToSubscriptionText = t('common.addToSubscription')

  const variantPreOrderDate = getMetafield(variant.metafields, 'product', 'pre_order_date')
  const preOrderButtonText = t('common.preorder')
  const preOrderInfoText = variantPreOrderDate
    ? t('pdp.preOrderText', {
        // date-fns doesn't have a shorter date version of the localized date
        // https://github.com/date-fns/date-fns/issues/1946
        date: new Date(variantPreOrderDate).toLocaleDateString(locale, {
          month: 'long',
          day: 'numeric',
        }),
      })
    : null

  const getIcon = (icon: ReactElement): ReactElement | undefined => {
    if (isBusy) return <IconLoading stroke={1.6} />
    if (showCheck) return <IconCheck stroke={1.6} />
    if (variantAvailable) return icon
    return undefined
  }

  const isSubscriptionPurchase = purchaseType === PurchaseType.subscription

  const getAddToCartButtonText = () => {
    if (showCheck) return addToCartSuccessText
    if (!variantAvailable && !isBusy) return notAvailableText
    if (variantPreOrderDate) return preOrderButtonText
    if (isSubscriptionPurchase) return addToSubscriptionText
    return addToCartText
  }

  const addToCartButtonText = getAddToCartButtonText()

  const onAddToCart = async () => {
    if (isSubscriptionPurchase) {
      await addToSubscriptionAction({
        product,
        variant,
        quantity,
        sellingPlanInterval,
        sellingPlans,
        existingLines,
        addToCartMutationFn: addToCart,
        cartLinesUpdateMutationFn: cartLinesUpdate,
        cartDiscountCodesUpdateMutationFn: cartDiscountCodesUpdate,
      })
    } else {
      await addToCartAction({
        product,
        variant,
        quantity,
        addToCartMutationFn: addToCart,
        cartId: cart?.id,
      })
    }
    setShowCheck(true)
    setTimer(() => setShowCheck(false))
  }

  return (
    <div className={clsx(styles.container, isRenderedWithinModal && styles.modalBuyBox)}>
      <div className={styles.cartButtonRow}>
        <Stepper value={quantity} onChange={setQuantity} disabled={!variantAvailable} />
        <BuyBoxPrice variant={variant} isSubscription={isSubscriptionPurchase} productType={productType} />
      </div>

      <Button
        onClick={onAddToCart}
        variant={variantAvailable ? 'primary-cta' : 'secondary'}
        className={clsx({
          [styles.cartButton]: true,
          [styles.subscriptionButton]: isSubscriptionPurchase,
          [styles.success]: showCheck,
        })}
        startIcon={isSubscriptionPurchase ? getIcon(<IconCalendarPlus />) : getIcon(<IconShoppingBag />)}
        disabled={!variantAvailable}
        fullWidth
        data-analytics-id={isSubscriptionPurchase ? 'add-to-cart-subscription' : 'add-to-cart'}
      >
        {addToCartButtonText}
      </Button>

      {productType === 'gear' && t('pdp.buyboxfootnote') && (
        <Text variant="p" small className={clsx(styles.notice, styles.secondaryText)}>
          {t('pdp.buyboxfootnote')}
        </Text>
      )}

      {preOrderInfoText && (
        <Alert variant="info" suppressHydrationWarning>
          {preOrderInfoText}
        </Alert>
      )}

      {getMetafield(variant.metafields, 'product', 'archived') && (
        <Alert variant="warning">{t('pdp.archivedText')}</Alert>
      )}

      {variant.requiresShipping && (
        <ShippingInfo
          variant={variant}
          quantity={quantity}
          setShowShippingTermsModal={setShowShippingTermsModal}
          isRenderedWithinModal={isRenderedWithinModal}
          isAddingToSubscription={isSubscriptionPurchase}
        />
      )}

      <Modal
        title={t('common.shippingTermsModal.title')}
        opened={showShippingTermsModal}
        onClose={() => setShowShippingTermsModal(false)}
      >
        <div dangerouslySetInnerHTML={{ __html: t('common.shippingTermsModal.body') }} suppressHydrationWarning />
      </Modal>
    </div>
  )
}
